/**
 * EAA-1825: one authenticated `fetch` for every `request()` helper in `src/api`.
 *
 * WHY: `AuthProvider` captured the access token into React state once per route
 * change, while oidc-client-ts kept renewing it silently in the background. After the
 * realm's access-token lifespan the state held a token Keycloak had already expired;
 * every console request then went out with it, portal-api answered 401 (code 1006,
 * "SSO token has expired"), and the screen said "Không thể tải danh sách" until F5
 * re-read the fresh token. The provider now keeps the token current (it subscribes to
 * `onUserLoaded`), and this helper closes the remaining gap: a request that still
 * meets a 401 asks the registered refresher for a new token ONCE and retries. A
 * second 401 is a real session end and is surfaced unchanged, so the loop guard in
 * `AuthProvider.reauthenticate` keeps its meaning.
 */
export type TokenRefresher = () => Promise<string | null>;

let refresher: TokenRefresher | null = null;
let inFlight: Promise<string | null> | null = null;

/** Registered by `AuthProvider`; returns the unregister function. */
export function registerTokenRefresher(fn: TokenRefresher): () => void {
    refresher = fn;
    return () => {
        if (refresher === fn) refresher = null;
    };
}

/** Coalesces concurrent refreshes so ten parallel 401s cost one silent renew. */
function refreshOnce(): Promise<string | null> {
    if (!refresher) return Promise.resolve(null);
    if (!inFlight) {
        inFlight = refresher()
            .catch(() => null)
            .finally(() => {
                inFlight = null;
            });
    }
    return inFlight;
}

export interface AuthFetchInit extends Omit<RequestInit, 'headers'> {
    headers?: Record<string, string>;
}

/**
 * `fetch` with `Authorization: Bearer <token>`. On a 401, refreshes the token through
 * the registered refresher and retries exactly once when a different token came back.
 */
export async function authFetch(url: string, token: string, init: AuthFetchInit = {}): Promise<Response> {
    const send = (bearer: string) =>
        fetch(url, { ...init, headers: { ...(init.headers ?? {}), Authorization: `Bearer ${bearer}` } });
    const res = await send(token);
    if (res.status !== 401) return res;
    const fresh = await refreshOnce();
    if (!fresh || fresh === token) return res;
    return send(fresh);
}
